import React, { useState, useEffect, useCallback } from "react";
import confetti from "canvas-confetti";
import "./JamieRsvp.css";

const FLOATING_EMOJIS = ["🍄", "🪴", "🍸", "🧓", "🌿", "🫒", "🍄‍🟫", "🪻", "🐈", "👵", "🍸", "🌱"];

const NAME_COLORS = [
  "#fff", "#ffe0f0", "#ffb6d9", "#fff5fa", "#ffc0e0",
  "#ffffff", "#ffd6ec", "#ffe0f0", "#fff", "#ffcce6",
];

const API_URL = process.env.REACT_APP_API_URL || "";
const EVENT_ID = "jamie-housewarming";

function fireConfetti() {
  confetti({
    particleCount: 80,
    spread: 100,
    origin: { y: 0.6 },
    colors: ["#ff69b4", "#ff1493", "#c71585", "#fff", "#ffb6d9"],
    shapes: ["circle"],
  });
}

export default function JamieRsvp() {
  const [name, setName] = useState("");
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const fetchRsvps = useCallback(async () => {
    if (!API_URL) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}?event=${EVENT_ID}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setRsvps(data);
      }
    } catch (err) {
      console.error("Failed to fetch RSVPs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRsvps();
  }, [fetchRsvps]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setError("");
    setSuccess("");

    if (!API_URL) {
      setRsvps((prev) => [
        ...prev,
        { id: Date.now().toString(), name: trimmed, timestamp: new Date().toISOString() },
      ]);
      setSuccess(`${trimmed} is coming! 🍄`);
      setName("");
      setSubmitting(false);
      fireConfetti();
      setTimeout(() => setSuccess(""), 4000);
      return;
    }

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed, event: EVENT_ID }),
      });
      if (!res.ok) throw new Error("Failed to RSVP");

      const newRsvp = await res.json().catch(() => ({
        id: Date.now().toString(), name: trimmed, timestamp: new Date().toISOString(),
      }));
      setRsvps((prev) => [...prev, newRsvp]);
      setSuccess(`${trimmed} is coming! 🍄`);
      setName("");
      fireConfetti();
      fetchRsvps().catch(() => {});
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setRsvps((prev) => [
        ...prev,
        { id: Date.now().toString(), name: trimmed, timestamp: new Date().toISOString() },
      ]);
      setSuccess(`${trimmed} is coming! 🍄`);
      setName("");
      fireConfetti();
      setTimeout(() => setSuccess(""), 4000);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="jamie-container">
      {/* Marquee */}
      <div className="jamie-marquee">
        <span className="jamie-marquee-text">
          🍄 OLD HOUSE OLD LADY 🍸 MUSHROOMS & MARTINIS 🪴 OLD HOUSE OLD LADY 🧓 MUSHROOMS & MARTINIS 🌿 OLD HOUSE OLD LADY 🍸 MUSHROOMS & MARTINIS 🍄 OLD HOUSE OLD LADY 🪴 MUSHROOMS & MARTINIS 🧓
        </span>
      </div>

      {/* Floating emojis */}
      {FLOATING_EMOJIS.map((emoji, i) => (
        <span key={i} className={`jamie-float jf-${(i % 8) + 1}`}>
          {emoji}
        </span>
      ))}

      {/* Main content */}
      <div className="jamie-content">
        <h1 className="jamie-title">
          Old House, Old Lady
          <em>mushrooms & martinis</em>
        </h1>

        <p className="jamie-subtitle">Jamie's Housewarming / Birthday 🍄🍸</p>

        <div className="jamie-details">
          <p className="jd-date">📅 Friday, May 30th</p>
          <p className="jd-time">🕗 8:00 PM</p>
          <p className="jd-place">📍 Jamie's Place</p>
          <p className="jd-theme">🌿 dress code: whatever your grandma would wear</p>
        </div>

        <p className="jamie-cta">🪴 drop your name to rsvp 🪴</p>

        {/* RSVP Form */}
        <form className="jamie-form" onSubmit={handleSubmit}>
          <input
            className="jamie-input"
            type="text"
            placeholder="your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            disabled={submitting}
          />
          <button
            className="jamie-button"
            type="submit"
            disabled={submitting || !name.trim()}
          >
            {submitting ? "adding..." : "I'll be there 🍸"}
          </button>
        </form>

        {success && <p className="jamie-success">{success}</p>}
        {error && <p className="jamie-error">{error}</p>}

        {/* RSVP List */}
        <div className="jamie-rsvp-section">
          <h2>🌿 Guest List 🌿</h2>
          {loading ? (
            <p className="jamie-loading">loading...</p>
          ) : rsvps.length === 0 ? (
            <p className="jamie-rsvp-empty">no one yet... be the first 🍄</p>
          ) : (
            <>
              <ul className="jamie-rsvp-list">
                {rsvps.map((rsvp, idx) => (
                  <li
                    key={rsvp.id}
                    className="jamie-rsvp-name"
                    style={{ color: NAME_COLORS[idx % NAME_COLORS.length] }}
                  >
                    {rsvp.name}
                  </li>
                ))}
              </ul>
              <p className="jamie-rsvp-count">
                {rsvps.length} guest{rsvps.length !== 1 ? "s" : ""} coming 🧓
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
